import type { ErrorOption, FieldPath, SubmitHandler } from "react-hook-form"

import type {
  CategoryCreateFailure,
  CategoryCreateInput,
  CategoryUpdateFailure,
  CategoryUpdateInput,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import useProductCategory from "@/screens/ManageProducts/components/ProductCategories/hooks/useProductCategory"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { normalizeToUpperCase } from "@/utils/utils"

interface UseProductCategoryActionsProps {
  name?: string
  onSuccess?: () => void
  onClose?: () => void
  setError: (name: FieldPath<CategoryCreateInput | CategoryUpdateInput>, error: ErrorOption) => void
}

const useProductCategoryActions = ({
  name,
  onSuccess,
  onClose,
  setError,
}: UseProductCategoryActionsProps): {
  createProductCategory: SubmitHandler<CategoryCreateInput>
  updateProductCategory: SubmitHandler<CategoryUpdateInput>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const shouldQuery = name?.length && name.length >= 2

  const { createCategory, updateCategory } = useProductCategory({
    query: name ?? "",
    pause: !shouldQuery,
  })

  const createProductCategory = async (input: CategoryCreateInput) => {
    const { error, data } = await createCategory({
      input: {
        name: normalizeToUpperCase(input.name),
      },
    })

    const hasError =
      error?.message ||
      (data?.category.create.__typename === "CategoryCreateFailure" && Boolean(data?.category.create.error))

    if (hasError) {
      setError("name", {
        type: "server",
        message: error?.message ?? (data?.category.create as CategoryCreateFailure).error?.message,
      })
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to create category`,
      })
      return
    }

    onSuccess?.()
    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Product category successfully created",
    })
    onClose?.()
  }

  const updateProductCategory = async (input: CategoryUpdateInput) => {
    const { error, data } = await updateCategory({
      input: {
        ...input,
        name: normalizeToUpperCase(input.name),
      },
    })

    const hasError =
      error?.message ||
      (data?.category.update.__typename === "CategoryUpdateFailure" && Boolean(data?.category.update.error))

    if (hasError) {
      setError("name", {
        type: "server",
        message: error?.message ?? (data?.category.update as CategoryUpdateFailure).error?.message,
      })
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to update category`,
      })
      return
    }

    onSuccess?.()
    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Product category successfully updated",
    })
    onClose?.()
  }

  return {
    createProductCategory,
    updateProductCategory,
  }
}

export default useProductCategoryActions
